import { JsonHalResource } from './jsonhal.model';
import { Platform } from './common';
import { DetectionRuleSeverity } from './finding.model';
import { IFetchContentInfo } from 'src/app/services/httpServices/dataLayerHttpService/data-layer-http.service';

// Backend DataItemType enum values (matches DataItemType in backend)
export enum DataLayerItemType {
  file = 'file',
  website = 'website',
  email = 'email',
  messages = 'messages',
  unknown = 'unknown',
  // Legacy values for backwards compatibility
  database = 'database',
  dataSource = 'dataSource',
  knowledgeBase = 'knowledgeBase',
  document = 'document',
  spreadsheet = 'spreadsheet',
  presentation = 'presentation',
  sharepoint = 'sharepoint',
}

// Backend DataItemSource enum values (matches DataItemSource in backend)
export enum DataSourceType {
  cloudShare = 'cloud_share',
  website = 'website',
  unknown = 'unknown',
  // Legacy values for backwards compatibility
  fileUpload = 'file-upload',
  cloudFile = 'cloud-file',
  database = 'database',
  sharepoint = 'sharepoint',
  fileSystem = 'fileSystem',
  sharePoint = 'sharePoint',
  oneDrive = 'oneDrive',
  teams = 'teams',
  email = 'email',
  api = 'api',
  vector = 'vector',
  embedding = 'embedding',
  other = 'other',
}

export enum DataAccessStatus {
  active = 'active',
  blocked = 'blocked',
  monitored = 'monitored',
  quarantined = 'quarantined',
  pending = 'pending',
}

// Data Layer Item interface - matches backend DataItemApiModel
// Supports both snake_case (API response) and camelCase (legacy) field names
export interface IDataLayerItem extends JsonHalResource {
  // Required fields from API
  readonly id: string;
  readonly resourceName: string;
  readonly dataType: DataLayerItemType;
  readonly sourceType: DataSourceType;
  readonly counter: number;
  readonly last_access_at: Date;
  readonly file_extension: string | null;
  readonly sensitivity_label: string | null;
  readonly platform: Platform;
  readonly fileExtension?: string | null;
  readonly lastAccessed: Date;
  readonly firstAccessed: Date;
  readonly accessCount: number;
  readonly sensitivityLabel?: string | null; // Raw sensitivity label string from API
  readonly status: DataAccessStatus;
  readonly tags: string[]; // file extension from API
  readonly metadata: Record<string, unknown>;
  readonly created: Date;
  readonly resourceId: string;
  readonly url?: string | null;

  // Optional fields for future extension (not currently in API)
  readonly size?: number;
  readonly location?: string;
  readonly owner?: string;
  readonly modified?: Date;
  readonly classification?: string;
  readonly complianceStatus?: string;

  // Relationships to existing models (optional, enriched from other sources)
  readonly relatedSteps?: string[]; // Links to agent steps that accessed this data
  readonly integrationId?: string; // Links to integration
  readonly findings?: string[]; // Links to security findings related to this data
  readonly findingsBySeverity?: Record<DetectionRuleSeverity, number>; // Findings grouped by severity

  // Fetch content info ( supported only for Copilot Studio items)
  readonly fetchContentInfo?: IFetchContentInfo | null;
  readonly service?: string | null;
}

// TODO. Aggregated analytics interface (mock for now)
export interface IDataLayerAnalytics {
  readonly totalDataSources: number;
  readonly totalDataAccess: number;
  readonly sensitiveDataAccess: number;
  readonly blockedAccess: number;
  readonly platformDistribution: Record<Platform, number>;
  readonly dataTypeDistribution: Record<DataLayerItemType, number>;
}

// Analytics interfaces for data item details
export interface IPlatformAccessByDate {
  readonly date: string;
  readonly service: string;
  readonly count: number;
}

export interface IDataItemAccessAnalytics {
  readonly dataItemId: string;
  readonly platformAccessByDate: IPlatformAccessByDate[];
  readonly totalAccessCount: number;
  readonly dateRangeStart: string;
  readonly dateRangeEnd: string;
}

export interface IDataItemTopAnalytics {
  readonly dataItemId: string;
  readonly topAgents: ITopAgent[];
  readonly otherAgentsCount: number;
  readonly topUsers: ITopUser[];
  readonly otherUsersCount: number;
}

export interface ITopAgent {
  readonly agentId: string;
  readonly agentName: string;
  readonly count: number;
}

export interface ITopUser {
  readonly userId: string;
  readonly userDisplayName: string;
  readonly count: number;
}

// API Response types for /v1-beta/detection/data-lens/items endpoint
export type DataLensItemType = 'file' | 'website' | 'email' | 'messages' | 'unknown';
export type DataLensSourceType = 'cloud_share' | 'website' | 'unknown';
